import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { Collapse } from 'reactstrap';
import { useIntl } from 'react-intl';

import { canSeeComments } from '../../../helpers/Comments';
import { getTaxonomyRoleSetting } from '../../../helpers/Metadata';

import FileGeneralInfo from '../GeneralInfo';
import FileFaces from './Faces';
import FileEditions from './Editions';
import FileTaxonomy from '../Taxonomy';
import FileComments from '../Comments';
import FileHistory from '../History';
import FileVersions from './Versions';
import FileTranscription from './Transcription';
import FileTaxonomiesExpandSwitch from './TaxonomiesExpandSwitch';
import FileEmptyFieldsSwitch from './EmptyFieldsSwitch';
import FileHistorySwitch from './FileHistorySwitch';
import FileSidebarNav from './Nav';
import LoadingSmall from '../../LoadingSmall';

const FileSidebar = ({
  handleSidebarTab,
  sidebarTab,
  currentFile,
  fileEdition,
  fileVersion,
  shareUuid,
  taxonomies,
  metadataGroups,
  showEmptyFields,
  showEmptyCustomFields,
  infoLoading,
  fileLog,
  fileViews,
  hasEmptyTaxonomies,
  handleFileEdition,
  handleSwitchEmptyFields,
  handleSwitchEmptyCustomFields,
  updateCommentsCount,
  switchEdition,
  switchVersion,
  currentVersionId,
  afterCreateVersion,
  afterCreateEdition,
  customFields,
  readOnly,
}) => {
  const intl = useIntl();

  const showComments = canSeeComments();
  const showTags = !shareUuid || (currentFile.terms && currentFile.terms.length > 0);

  const [taxonomiesExpanded, setTaxonomiesExpanded] = useState({});
  const [fullEditionsLoaded, setFullEditionsLoaded] = useState(false);

  const handleExpandTaxonomy = (taxonomyId) => {
    setTaxonomiesExpanded(prevTaxonomies => {
      const prevValue = prevTaxonomies[taxonomyId] ?? true;
      return { ...prevTaxonomies, [taxonomyId]: !prevValue };
    });
  };

  const allTaxonomiesExpanded = !taxonomies
    ?.some(t => taxonomiesExpanded[t.id] === false);

  const handleSwitchAllTaxonomies = () => {
    if (allTaxonomiesExpanded) {
      const newTaxonomiesExpanded = taxonomies.reduce((acc, t) => {
        acc[t.id] = false;
        return acc;
      }, {});
      setTaxonomiesExpanded(newTaxonomiesExpanded);
    } else {
      setTaxonomiesExpanded({});
    }
  };

  const handleFullEditionsLoaded = (value) => {
    setFullEditionsLoaded(value);
  };

  const metadataGroupToDisplay = String(
    fileEdition
      ? fileEdition.metadata_group?.id
      : currentFile.metadata_group?.id,
  );

  const getTaxonomiesToDisplay = () => {
    if (!metadataGroupToDisplay) return taxonomies;

    const metadataGroup = metadataGroups.find(
      mg => `${mg.id}` === `${metadataGroupToDisplay}`,
    );

    const aiTaggingTaxonomies = taxonomies.filter(t => t.ai_tagging);

    const taxonomiesWithoutGroups = taxonomies.filter(
      t => !t.ai_tagging && t.metadata_groups?.length === 0,
    );

    const groupedTaxonomies = taxonomies
      .filter(t => !t.ai_tagging)
      .filter(t => metadataGroup?.taxonomies.includes(t.id))
      .sort((a, b) => {
        const order = metadataGroup?.taxonomies || [];
        return order.indexOf(a.id) - order.indexOf(b.id);
      });

    return [
      ...aiTaggingTaxonomies,
      ...groupedTaxonomies,
      ...taxonomiesWithoutGroups,
    ];
  };

  const taxonomiesToDisplay = getTaxonomiesToDisplay();

  return (
    <aside className={classnames('file-sidebar')}>
      <div className="file-sidebar-inner">
        <FileSidebarNav
          file={currentFile}
          handleSidebarTab={handleSidebarTab}
          shareUuid={shareUuid}
          sidebarTab={sidebarTab}
          showComments={showComments}
          showTags={showTags}
          fullEditionsLoaded={fullEditionsLoaded}
        />
        <div className="file-sidebar-content">
          {(sidebarTab === 'details' || sidebarTab === 'history') && (
            <>
              <Collapse isOpen={sidebarTab === 'details'}>
                <div className="file-sidebar-content-scrollable">
                  <FileGeneralInfo
                    file={currentFile}
                    fileEdition={fileEdition}
                    handleFileEdition={handleFileEdition}
                    fileVersion={fileVersion}
                    shareUuid={shareUuid}
                    sidebarTab={sidebarTab}
                    fileViews={fileViews}
                    showEmptyCustomFields={showEmptyCustomFields}
                    readOnly={readOnly}
                  />
                </div>
              </Collapse>
              <>
                <div className="file-history-switch file-details-item">
                  <div className="file-detail-header file-detail-header-buttons-wrapper">
                    {!shareUuid && (
                      <FileHistorySwitch
                        sidebarTab={sidebarTab}
                        handleSidebarTab={handleSidebarTab}
                      />
                    )}
                    {(sidebarTab !== 'history' && customFields.length > 0) && (
                      <FileEmptyFieldsSwitch
                        showEmptyFields={showEmptyCustomFields}
                        handleSwitchEmptyFields={handleSwitchEmptyCustomFields}
                        hideMessage={intl.formatMessage({ defaultMessage: 'Hide empty fields' })}
                        showMessage={intl.formatMessage({ defaultMessage: 'Show empty fields' })}
                        settingName="show_empty_custom_fields"
                      />
                    )}
                  </div>
                </div>
                <Collapse
                  key={`file-history-collapse-with-${sidebarTab}`}
                  isOpen={sidebarTab === 'history'}
                  className="file-history-collapse"
                >
                  <div className="file-sidebar-content-scrollable">
                    <FileHistory
                      file={currentFile}
                      logs={fileLog}
                      loading={infoLoading}
                    />
                  </div>
                </Collapse>
              </>
            </>
          )}
          {sidebarTab === 'people' && (
            <FileFaces
              file={currentFile}
              readOnly={readOnly}
            />
          )}
          {!shareUuid && sidebarTab === 'editions' && (
            <FileEditions
              file={currentFile}
              fileEdition={fileEdition}
              fileVersion={fileVersion}
              switchEdition={switchEdition}
              afterCreateVersion={afterCreateVersion}
              afterCreateEdition={afterCreateEdition}
              updateCommentsCount={updateCommentsCount}
              handleFullEditionsLoaded={handleFullEditionsLoaded}
            />
          )}
          {sidebarTab === 'tags' && (
            <>
              <div className="file-sidebar-content-scrollable">
                {taxonomiesToDisplay.map(taxonomy => (
                  <FileTaxonomy
                    key={`file-taxonomy-${taxonomy.id}`}
                    file={fileEdition || currentFile}
                    taxonomy={taxonomy}
                    showEmptyFields={showEmptyFields && !shareUuid}
                    open={taxonomiesExpanded[taxonomy.id] ?? true}
                    handleExpandTaxonomy={handleExpandTaxonomy}
                    readOnly={readOnly || getTaxonomyRoleSetting(taxonomy, 'read_only') === true}
                  />
                ))}
              </div>
              <div className="file-empty-fields file-details-item">
                <div className="file-detail-header file-detail-header-buttons-wrapper">
                  <FileTaxonomiesExpandSwitch
                    handleSwitchAllTaxonomies={handleSwitchAllTaxonomies}
                    allTaxonomiesExpanded={allTaxonomiesExpanded}
                  />
                  {hasEmptyTaxonomies() && (
                    <FileEmptyFieldsSwitch
                      showEmptyFields={showEmptyFields}
                      handleSwitchEmptyFields={handleSwitchEmptyFields}
                      hideMessage={intl.formatMessage({ defaultMessage: 'Hide empty categories' })}
                      showMessage={intl.formatMessage({ defaultMessage: 'Show empty categories' })}
                      settingName="show_empty_fields"
                    />
                  )}
                </div>
              </div>
            </>
          )}
          {(sidebarTab === 'versions') && (
            <FileVersions
              file={currentFile}
              switchVersion={switchVersion}
              currentVersionId={currentVersionId}
              afterCreateVersion={afterCreateVersion}
              updateCommentsCount={updateCommentsCount}
              readOnly={readOnly}
            />
          )}
          <FileComments
            file={currentFile}
            fileVersion={fileVersion}
            updateCommentsCount={updateCommentsCount}
            handleSidebarTab={handleSidebarTab}
            show={sidebarTab === 'comments'}
            readOnly={readOnly}
          />
          {(sidebarTab === 'transcription') && (
            <>
              {currentFile.extra_fields ? (
                <FileTranscription
                  file={currentFile}
                  readOnly={readOnly}
                />
              ) : (
                infoLoading && (
                  <LoadingSmall />
                )
              )}
            </>
          )}
        </div>
      </div>
    </aside>
  );
};

const mapStateToProps = state => ({
  customFields: state.Metadata.customFields,
  taxonomies: state.Metadata.taxonomies,
  metadataGroups: state.Metadata.metadataGroups,
  filesLoading: state.Files.loading,
});

FileSidebar.propTypes = {
  handleSidebarTab: PropTypes.func.isRequired,
  sidebarTab: PropTypes.string.isRequired,
  currentFile: PropTypes.object.isRequired,
  fileEdition: PropTypes.object,
  fileVersion: PropTypes.number,
  shareUuid: PropTypes.string,
  taxonomies: PropTypes.array.isRequired,
  metadataGroups: PropTypes.array.isRequired,
  showEmptyFields: PropTypes.bool.isRequired,
  showEmptyCustomFields: PropTypes.bool.isRequired,
  infoLoading: PropTypes.bool.isRequired,
  fileLog: PropTypes.array.isRequired,
  fileViews: PropTypes.number,
  hasEmptyTaxonomies: PropTypes.func.isRequired,
  handleFileEdition: PropTypes.func.isRequired,
  switchEdition: PropTypes.func.isRequired,
  switchVersion: PropTypes.func.isRequired,
  handleSwitchEmptyFields: PropTypes.func.isRequired,
  handleSwitchEmptyCustomFields: PropTypes.func.isRequired,
  updateCommentsCount: PropTypes.func.isRequired,
  currentVersionId: PropTypes.number,
  afterCreateVersion: PropTypes.func.isRequired,
  afterCreateEdition: PropTypes.func.isRequired,
  customFields: PropTypes.array.isRequired,
  readOnly: PropTypes.bool,
};

FileSidebar.defaultProps = {
  fileEdition: null,
  fileVersion: null,
  fileViews: null,
  shareUuid: null,
  currentVersionId: null,
  readOnly: false,
};

export default connect(mapStateToProps)(FileSidebar);
